// API service for user profile management
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

class ProfileService {
  static async getUserProfile(userId, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        // If user doesn't exist (404), the backend will auto-create them
        // due to the ensureUserExists middleware, so we can retry once
        if (response.status === 404) {
          console.log('User not found, backend should auto-create. Retrying...');
          // Small delay before retry
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          const retryResponse = await fetch(`${API_BASE_URL}/users/${userId}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          
          if (!retryResponse.ok) {
            throw new Error(`HTTP error! status: ${retryResponse.status}`);
          }
          
          const retryData = await retryResponse.json();
          return retryData.data;
        }
        
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data; // Assuming the API returns { success: true, data: user }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  }

  static async updateUserProfile(userId, profileData, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(profileData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }

  static async searchUsers(query, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/users/search?query=${encodeURIComponent(query)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data; // Assuming the API returns { success: true, data: users }
    } catch (error) {
      console.error('Error searching users:', error);
      throw error;
    }
  }

  static async getUsersByOrganization(organizationId, token, limit = 50, lastKey = null) {
    try {
      let url = `${API_BASE_URL}/users/organization/${organizationId}?limit=${limit}`;
      if (lastKey) {
        url += `&lastKey=${encodeURIComponent(JSON.stringify(lastKey))}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return {
        users: data.data,
        pagination: data.pagination
      };
    } catch (error) {
      console.error('Error fetching organization users:', error);
      throw error;
    }
  }
}

export default ProfileService;
